import { FieldError } from "@/shared/components/form/FieldError";
import { ColocaCombobox } from "@/shared/components/forms/ColocaCombobox";
import useTranslations from "@/shared/lang/useTranslations";
import { formatPixInput, PIX_KEY_META, resolvePixKeyType } from "@/shared/utils/pixKey";

import {
  FORM_METHOD_FIELD_LABEL_TKEYS,
  FORM_METHOD_OPTION_LABEL_KEYS,
} from "../constants/form-method-field-translations";
import {
  getPaymentMethodFields,
  isBinanceUidEmailExclusivePair,
} from "../constants/paymentMethodFields.config";
import type { PaymentMethodFields, PaymentMethodFieldConfig } from "../types/new-payment.types";

// Text inputs mirror the DS `Input` primitive token-for-token (h-9, border-input, dark:bg-input/30) so
// they sit flush with the `ColocaCombobox` selects. The selects are NOT native <select>s on purpose: the
// native option popup is OS-painted (white list / light-blue hover in dark mode) and cannot be themed
// reliably — the DS combobox renders a fully styled portal instead.
const inputBase =
  "h-9 w-full min-w-0 rounded-md border border-input bg-transparent px-2.5 py-1 text-base shadow-xs transition-[color,box-shadow] outline-none placeholder:text-muted-foreground focus:border-ring focus:ring-3 focus:ring-ring/50 disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 md:text-sm dark:bg-input/30";
const labelBase = "block text-sm font-medium text-muted-foreground";

type PaymentMethodFieldsSectionProps = {
  currency: string;
  method: string;
  value: PaymentMethodFields;
  onChange: (fieldId: string, fieldValue: string) => void;
  errors?: Partial<Record<string, string>>;
  disabled?: boolean;
};

function FieldInput({
  field,
  label,
  placeholder,
  options,
  selectPlaceholder,
  fieldValue,
  onChange,
  disabled,
  error,
  helper,
  formatValue,
}: {
  field: PaymentMethodFieldConfig;
  label: string;
  placeholder?: string;
  options?: { value: string; label: string }[];
  selectPlaceholder: string;
  fieldValue: string;
  onChange: (v: string) => void;
  disabled: boolean;
  error?: string;
  /** Muted hint below the input, shown while there is no error (e.g. the PIX key format). */
  helper?: string;
  /** Display mask applied on every keystroke (e.g. the progressive CPF/CNPJ/phone PIX mask). */
  formatValue?: (v: string) => string;
}) {
  const inputId = `payment-method-${field.id}`;
  if (field.type === "select") {
    return (
      <div className="space-y-2">
        <label htmlFor={inputId} className={labelBase}>
          {label}
        </label>
        <ColocaCombobox
          id={inputId}
          value={fieldValue || null}
          onValueChange={(v) => onChange(v ?? "")}
          options={options ?? []}
          placeholder={placeholder ?? selectPlaceholder}
          disabled={disabled}
          className="w-full"
        />
        <FieldError error={error} />
      </div>
    );
  }
  return (
    <div className="space-y-2">
      <label htmlFor={inputId} className={labelBase}>
        {label}
      </label>
      <input
        id={inputId}
        type={field.type}
        value={fieldValue}
        onChange={(e) => {
          let v = e.target.value;
          if (field.digitsOnly) v = v.replace(/\D/g, "");
          if (formatValue) v = formatValue(v);
          onChange(v);
        }}
        placeholder={placeholder ?? field.placeholder}
        disabled={disabled}
        className={inputBase}
        inputMode={field.digitsOnly ? "numeric" : undefined}
        autoComplete={field.type === "email" ? "email" : undefined}
      />
      <FieldError error={error} />
      {!error && helper && <p className="text-[13px] text-muted-foreground">{helper}</p>}
    </div>
  );
}

export function PaymentMethodFieldsSection({
  currency,
  method,
  value,
  onChange,
  errors = {},
  disabled = false,
}: PaymentMethodFieldsSectionProps) {
  const { t } = useTranslations("payments");
  // PIX placeholder/helper copy is shared across modules via `PIX_KEY_META` (global-namespace keys).
  const { t: tGlobal } = useTranslations("global");
  const fields = getPaymentMethodFields(currency, method);
  if (fields.length === 0) return null;

  // BRL|Pix mirrors the one-off recipient panel: the key input masks per selected type, shows the exact
  // format as placeholder + helper, and stays locked until a type is chosen.
  const isPixMethod = currency.trim() === "BRL" && method.trim() === "Pix";
  const pixType = isPixMethod ? resolvePixKeyType(value.pix_type) : undefined;
  const pixMeta = pixType ? PIX_KEY_META[pixType] : undefined;

  return (
    <div className="space-y-6">
      <h3 className="text-base font-semibold text-foreground">{t("form_method_section_title")}</h3>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {fields.map((field) => {
          const labelKey = FORM_METHOD_FIELD_LABEL_TKEYS[field.id];
          const translatedLabel = labelKey ? t(labelKey) : field.label;
          const translatedOptions = field.options?.map((opt) => ({
            value: opt.value,
            label: FORM_METHOD_OPTION_LABEL_KEYS[opt.value]
              ? t(FORM_METHOD_OPTION_LABEL_KEYS[opt.value])
              : opt.label,
          }));
          const selectPlaceholder = t("form_method_select_placeholder", {
            label: translatedLabel.toLowerCase(),
          });
          const uidTrim = (value.binancePayUid ?? "").trim();
          const emailTrim = (value.binancePayEmail ?? "").trim();
          const binanceSiblingLocksOther =
            isBinanceUidEmailExclusivePair(currency, method) &&
            ((field.id === "binancePayUid" && emailTrim !== "") ||
              (field.id === "binancePayEmail" && uidTrim !== ""));
          const isPixKeyField = isPixMethod && field.id === "pix_key";
          return (
            <FieldInput
              key={field.id}
              field={field}
              label={translatedLabel}
              placeholder={
                isPixKeyField
                  ? pixMeta
                    ? tGlobal(pixMeta.ph)
                    : t("form_method_pix_select_type_first")
                  : field.placeholder
              }
              helper={isPixKeyField && pixMeta ? tGlobal(pixMeta.helper) : undefined}
              formatValue={isPixKeyField && pixType ? (v) => formatPixInput(pixType, v) : undefined}
              options={translatedOptions}
              selectPlaceholder={selectPlaceholder}
              fieldValue={value[field.id] ?? ""}
              onChange={(v) => {
                onChange(field.id, v);
                // Switching the key type restarts the key — mirrors the one-off recipient panel.
                if (isPixMethod && field.id === "pix_type" && v !== (value.pix_type ?? "")) {
                  onChange("pix_key", "");
                }
              }}
              disabled={disabled || binanceSiblingLocksOther || (isPixKeyField && !pixType)}
              error={errors[field.id]}
            />
          );
        })}
      </div>
    </div>
  );
}
